import { PanoGroupIcon, PhotoGroupIcon } from '@/components/IconButton';
import { ANALYTICS_EVENT_OBJECT } from '@/constants/analytics';
import { queryClient } from '@/graphql/client';
import {
  UpdatePanoramicImagesStatusMutationVariables,
  UpdateProcessImagesStatusMutationVariables,
} from '@/graphql/codegen/graphql';
import {
  UPDATE_PANORAMIC_PROCESS_IMAGE_STATUS,
  UPDATE_PROCESS_IMAGES_STATUS,
} from '@/graphql/mutations';
import { request } from '@/graphql/request';
import { refetchProcesses, useFetchProcesses } from '@/hooks/useFetchProcesses';
import { Popover, PopoverContent, PopoverTrigger } from '@/next/components/Popover';
import { ToolBarIconButton } from '@/next/components/ToolBarIconButton';
import { screenSizeDetector } from '@/next/utils/screenSizeDetector';
import { useExplore } from '@/stores/explore';
import {
  Panorama,
  Photo2D,
  startDraw3D,
  startPanoramaWalkthrough,
  startPhotoOverlay,
  stopDraw3D,
  stopPanoramaView,
  stopPhotoOverlay,
  useViewer,
} from '@/stores/viewer';
import { cn } from '@/utils/classname';
import { getClosestTarget } from '@/utils/photos';
import { ProcessImage } from '@/utils/process';
import { getShareLinkToken } from '@/utils/shareLink';
import { AI_PREDICTOR, DRAW_RECT } from '@/utils/split';
import { Button, toast } from '@skand/ui';
import { useTreatments } from '@splitsoftware/splitio-react';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { SettingsMenu } from '../../../SettingsMenu';
import { GlobeControlMenu } from '../GlobeControlMenu';
import { DrawToolMenu } from './DrawToolMenu';

export interface ActionButtonsProps {
  togglePredictor: () => void;
  enabledPredictor: boolean;
}

export const ActionButtons = ({ togglePredictor, enabledPredictor }: ActionButtonsProps) => {
  const treatment = useTreatments([AI_PREDICTOR, DRAW_RECT]);
  const processTabFlag = treatment[AI_PREDICTOR].treatment === 'on';
  const drawRectFlag = treatment[DRAW_RECT].treatment === 'on';

  const projectId = useExplore(state => state.projectId);
  const api3D = useViewer(state => state.api3D);
  const enabledDraw3D = useViewer(state => state.enabledDraw3D);
  const enabledDraw2D = useViewer(state => state.enabledDraw2D);
  const photo2DGroups = useViewer(state => state.photo2DGroups);
  const panoramaGroups = useViewer(state => state.panoramaGroups);
  const enabledPhotoOverlay = useViewer(state => state.enabledPhotoOverlay);
  const enabledPanoramaWalkthrough = useViewer(state => state.enabledPanoramaWalkthrough);
  const enabled3DMode = !enabledPhotoOverlay && !enabledPanoramaWalkthrough;
  const loadingMutex = useViewer(state => state.loadingMutex);
  const navigationMode = useViewer(state => state.navigationMode);
  const projectionMode = useViewer(state => state.projectionMode);
  const photos = useViewer(state => state.ribbonPhotos);

  const hasShareLinkToken = getShareLinkToken();
  const annotationDraft = useExplore(state => state.annotationDraft);

  const targetProcess = useViewer(state => state.targetProcess);
  const targetPhoto = useViewer(state => state.targetPhoto);
  const processImage = targetProcess?.images.get(targetPhoto?.id ?? '');

  const updateProcesses = async () => {
    const processes = await refetchProcesses(projectId);
    useViewer.setState(prevState => {
      const currTarget = prevState.targetProcess;
      const targetProcess = currTarget
        ? processes.find(process => process.id === currTarget.id)
        : null;
      return { processes, targetProcess };
    });
  };

  const { isDesktop, isMobile } = screenSizeDetector.useScreenSize();

  useEffect(() => {
    api3D?.navigation.setNavigationMode(navigationMode);
    api3D?.navigation.setProjectionMode(projectionMode);
  }, [api3D?.navigation, navigationMode, projectionMode]);

  const updateAnnotationProcessImagesStatus = useMutation({
    mutationFn: (params: UpdateProcessImagesStatusMutationVariables) =>
      request(UPDATE_PROCESS_IMAGES_STATUS, params),
    onSuccess: async () => {
      await queryClient.invalidateQueries(useFetchProcesses.getQueryKey(projectId));
      await updateProcesses();
    },
  });

  const updatePanoramicProcessImagesStatus = useMutation({
    mutationFn: (params: UpdatePanoramicImagesStatusMutationVariables) =>
      request(UPDATE_PANORAMIC_PROCESS_IMAGE_STATUS, params),
    onSuccess: async () => {
      await queryClient.invalidateQueries(useFetchProcesses.getQueryKey(projectId));
      await updateProcesses();
    },
  });

  // Toggle the camera navigation controls
  const toggleNavigationMode = () => {
    if (navigationMode === 'orbit') {
      useViewer.setState({ navigationMode: 'superman' });
    } else {
      useViewer.setState({ navigationMode: 'orbit' });
    }
  };

  // Toggle the camera projection mode
  const toggleCameraProjection = () => {
    if (projectionMode === 'perspective') {
      useViewer.setState({ projectionMode: 'orthographic' });
    } else {
      useViewer.setState({ projectionMode: 'perspective' });
    }
  };

  // Toggle the draw tool
  const toggleDrawTool = () => {
    if (!enabledDraw3D) {
      startDraw3D();
    } else {
      stopDraw3D();
    }
  };

  // Enter 3D mode (stop image views)
  const handle3DMode = () => {
    if (!loadingMutex) {
      stopPhotoOverlay();
      stopPanoramaView();
    }
  };

  // Start the image overlay at prioritized image
  const handlePhotoOverlay = async () => {
    const { enabled2D } = useViewer.getState();
    if (!api3D || loadingMutex) return;

    const targets = photos.filter(photo => photo.type === 'photo2D' && photo.widget) as Photo2D[];
    if (targets.length > 0) {
      handle3DMode();
      startPhotoOverlay(targets[0], false, !enabled2D);
    } else {
      const cameraPosition = api3D.navigation.getPosition();
      const closestTarget = getClosestTarget(photo2DGroups, cameraPosition);
      if (closestTarget) {
        toast({
          type: 'info',
          message: 'No 2D image groups toggled. Opening the closest one.',
          lifespan: 5000,
        });
        handle3DMode();
        startPhotoOverlay(closestTarget as Photo2D);
        useViewer.setState(prev => {
          const visiblePhotoGroups = new Set(prev.visiblePhotoGroups);
          visiblePhotoGroups.add(closestTarget.group.id);
          return { visiblePhotoGroups };
        });
      } else {
        toast({
          type: 'info',
          message: 'Could not start image overlay. Toggle the layer/s with 2D images.',
          lifespan: 5000,
        });
      }
    }
  };

  // Start the panorama walkthrough at prioritized image
  const handlePanoramaWalkthrough = async () => {
    if (!api3D || loadingMutex) return;

    const targets = photos.filter(photo => photo.type === 'panorama' && photo.widget) as Panorama[];
    if (targets.length > 0) {
      handle3DMode();
      startPanoramaWalkthrough(targets[0]);
    } else {
      const cameraPosition = api3D.navigation.getPosition();
      const closestTarget = getClosestTarget(panoramaGroups, cameraPosition);
      if (closestTarget) {
        toast({
          type: 'info',
          message: 'No panorama groups toggled. Opening the closest one.',
          lifespan: 5000,
        });
        handle3DMode();
        startPanoramaWalkthrough(closestTarget as Panorama);
        useViewer.setState(prev => {
          const visiblePhotoGroups = new Set(prev.visiblePhotoGroups);
          visiblePhotoGroups.add(closestTarget.group.id);
          return { visiblePhotoGroups };
        });
      } else {
        toast({
          type: 'info',
          message:
            'Could not start panorama walkthrough. Toggle the layer/s with panoramic images.',
          lifespan: 5000,
        });
      }
    }
  };

  // Toggle the process image status
  const toggleProcessImageStatus = (status: ProcessImage['status']) => {
    if (targetProcess && processImage) {
      if (targetProcess.kind === 'annotation') {
        updateAnnotationProcessImagesStatus.mutate({
          annotationProcessImageStatusIds: [processImage.processImageId],
          status: status.toUpperCase(),
        });
      } else if (targetProcess.kind === 'panorama') {
        updatePanoramicProcessImagesStatus.mutate({
          panoramicProcessImageStatusIds: [processImage.processImageId],
          status: processImage.status === 'created' ? 'PLACED' : 'NOT_PLACED',
        });
      }
    }
  };

  const isDrawToolButtonDisabled =
    enabledDraw2D !== null || annotationDraft?.annotation3d !== undefined;

  return (
    <>
      <div
        className={cn(
          'absolute',
          isMobile
            ? 'bottom-0 bg-neutral-100 p-2 border-t-1 border-t-neutral-400 border-t-solid w-full'
            : 'top-3 right-0 mr-3',
        )}
      >
        <div
          className={cn(
            'flex',
            'items-center',
            'z-1',
            'pointer-events-auto',
            'space-x-10px',
            isMobile ? 'justify-between' : 'justify-start',
          )}
        >
          {processImage && (
            <>
              {processImage.status !== 'ignored' && processImage.status !== 'checked' && (
                <Button
                  active
                  className={cn(
                    'h-30px',
                    'b-1px! b-solid! b-neutral-100!',
                    'color-neutral-100!',
                    'cursor-pointer',
                  )}
                  filled
                  onClick={() => toggleProcessImageStatus('ignored')}
                  primary
                  size="small"
                >
                  Ignore
                </Button>
              )}
              {processImage.status !== 'checked' && processImage.status !== 'ignored' && (
                <Button
                  active
                  className={cn(
                    'h-30px',
                    'b-1px! b-solid! b-neutral-100!',
                    'color-neutral-100!',
                    'cursor-pointer',
                  )}
                  filled
                  onClick={() => toggleProcessImageStatus('checked')}
                  primary
                  size="small"
                >
                  Submit
                </Button>
              )}
              {processImage.status !== 'created' && (
                <Button
                  active
                  className={cn(
                    'h-30px',
                    'b-1px! b-solid! b-neutral-100!',
                    'color-neutral-100!',
                    'cursor-pointer',
                  )}
                  filled
                  onClick={() => toggleProcessImageStatus('created')}
                  primary
                  size="small"
                >
                  Reopen
                </Button>
              )}
            </>
          )}

          {isDesktop && enabledPhotoOverlay && !hasShareLinkToken && processTabFlag && (
            <div className={cn('relative')}>
              <Button
                className="cursor-pointer"
                filled
                onClick={togglePredictor}
                primary={enabledPredictor}
              >
                Predict
              </Button>
            </div>
          )}

          <div className={cn('relative')}>
            {!drawRectFlag || isMobile ? (
              <ToolBarIconButton
                analyticsEventObject={ANALYTICS_EVENT_OBJECT.DRAW_TOOL_3D}
                icon={<div className="i-skand-annotation text-18px" />}
                isActive={enabledDraw3D !== null}
                isDisabled={isDrawToolButtonDisabled}
                onClick={toggleDrawTool}
                tooltip="Draw tool"
              />
            ) : (
              <DrawToolMenu />
            )}
          </div>

          <div className={cn('relative')}>
            <ToolBarIconButton
              analyticsEventObject={
                projectionMode === 'perspective'
                  ? ANALYTICS_EVENT_OBJECT.ORTHOGRAPHIC_VIEW
                  : ANALYTICS_EVENT_OBJECT.PERSPECTIVE_VIEW
              }
              icon={
                <div
                  className={cn(
                    'text-18px',
                    projectionMode === 'perspective' ? 'i-skand-perspective' : 'i-skand-ortho',
                  )}
                />
              }
              isDisabled={enabledPanoramaWalkthrough || enabledPhotoOverlay}
              onClick={toggleCameraProjection}
              tooltip={projectionMode === 'perspective' ? 'Orthographic' : 'Perspective'}
            />
          </div>

          <div className={cn('relative')}>
            <ToolBarIconButton
              analyticsEventObject={
                navigationMode === 'superman'
                  ? ANALYTICS_EVENT_OBJECT.ORBIT_MODE
                  : ANALYTICS_EVENT_OBJECT.SUPERHERO_MODE
              }
              icon={
                <div
                  className={cn(
                    'text-18px',
                    navigationMode === 'superman' ? 'i-skand-superhero' : 'i-skand-orbit',
                  )}
                />
              }
              isDisabled={enabledPanoramaWalkthrough || enabledPhotoOverlay}
              onClick={toggleNavigationMode}
              tooltip={`Switch to ${
                navigationMode === 'superman' ? 'Orbit' : 'Superhero'
              } mode here`}
            />
          </div>

          <div className={cn('relative h-30px w-30px')}>
            <Popover>
              <PopoverTrigger className="group">
                <ToolBarIconButton
                  analyticsEventObject={ANALYTICS_EVENT_OBJECT.GLOBE_CONTROL_MENU}
                  icon={<div className="i-skand-map text-18px" />}
                  tooltip="Globe Control Menu"
                />
              </PopoverTrigger>
              <PopoverContent>
                <div className="max-h-[calc(100dvh-120px)] max-w-100dvw px-4">
                  <GlobeControlMenu />
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className={cn('relative h-30px w-30px')}>
            <Popover>
              <PopoverTrigger className="group">
                <ToolBarIconButton
                  analyticsEventObject={ANALYTICS_EVENT_OBJECT.VIEWER_SETTINGS}
                  icon={<div className="i-skand-gear text-18px" />}
                  tooltip="Settings"
                />
              </PopoverTrigger>
              <PopoverContent>
                <div className="px-4">
                  <SettingsMenu />
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {isDesktop && (
            <div className={cn('relative flex flex-row')}>
              <button
                className={cn(
                  'transition',
                  'duration-300',
                  'ease-out',
                  'h-[30px]',
                  'w-[30px]',
                  'flex',
                  'justify-center',
                  'items-center',
                  !enabled3DMode ? 'bg-transparent cursor-pointer' : 'bg-neutral-700',
                  'border-x-1 border-y-1',
                  'rounded-bl-1',
                  'rounded-tl-1',
                  'border-solid',
                  'fill-none',
                  'border-neutral-100',
                )}
                data-analytics-event-object={ANALYTICS_EVENT_OBJECT.VIEW_3D}
                disabled={enabled3DMode}
                onClick={handle3DMode}
              >
                <div className={cn('color-neutral-100 typo-text-s')}>3D</div>
              </button>
              <button
                className={cn(
                  'transition',
                  'duration-300',
                  'ease-out',
                  'h-[30px]',
                  'w-[30px]',
                  'flex',
                  'justify-center',
                  'items-center',
                  !enabledPanoramaWalkthrough ? 'bg-transparent cursor-pointer' : 'bg-neutral-700',
                  'border-l-0 border-r-1 border-y-1',
                  'border-solid',
                  'fill-none',
                  'border-neutral-100',
                )}
                data-analytics-event-object={ANALYTICS_EVENT_OBJECT.VIEW_PANORAMA}
                disabled={enabledPanoramaWalkthrough}
                onClick={handlePanoramaWalkthrough}
              >
                <PanoGroupIcon className="color-neutral-100" />
              </button>
              <button
                className={cn(
                  'transition',
                  'duration-300',
                  'ease-out',
                  'h-[30px]',
                  'w-[30px]',
                  'flex',
                  'justify-center',
                  'items-center',
                  !enabledPhotoOverlay ? 'bg-transparent cursor-pointer' : 'bg-neutral-700',
                  'border-l-0 border-r-1 border-y-1',
                  'rounded-br-1',
                  'rounded-tr-1',
                  'border-solid',
                  'fill-none',
                  'border-neutral-100',
                )}
                data-analytics-event-object={ANALYTICS_EVENT_OBJECT.IMAGE_VIEWER_2D_IMAGE}
                disabled={enabledPhotoOverlay}
                onClick={handlePhotoOverlay}
              >
                <PhotoGroupIcon className="color-neutral-100" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
